import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Link, useLocation } from 'react-router-dom';
import { FiRefreshCw, FiChevronRight } from 'react-icons/fi';
import DashboardNav from './DashboardNav';
// DashboardTicker lives in DashboardLayout (App.tsx) — stays mounted across dashboard navigation

export type DateRange = '7d' | '30d' | '90d';

interface DashboardShellProps {
  title: string;
  children: (dateRange: DateRange) => React.ReactNode;
  onRefresh?: () => void;
}

const BOARD_SUBTITLES: Record<string, string> = {
  'The Pulse': 'The heartbeat — everything at a glance',
  'Forge Monitor': 'Watching the forge — who\'s rising, who needs work',
  'Phase Watch': 'Monitoring the assembly line — what\'s running, what\'s stuck',
  'Blueprint Vault': 'Surveying the architecture — hydration, health, provenance',
};

const BOARD_GRADIENTS: Record<string, string> = {
  'The Pulse': 'from-indigo-400 to-purple-500',
  'Forge Monitor': 'from-purple-400 to-violet-500',
  'Phase Watch': 'from-blue-400 to-cyan-500',
  'Blueprint Vault': 'from-cyan-400 to-teal-500',
};

const DashboardShell: React.FC<DashboardShellProps> = ({ title, children, onRefresh }) => {
  const { darkMode } = useTheme();
  const location = useLocation();
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const handleRefresh = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(handleRefresh, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, handleRefresh]);

  // Build breadcrumbs from path — collapse /user/{uuid} into "My Dashboard"
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const pathParts = location.pathname.split('/').filter(Boolean);
  const breadcrumbs: { label: string; path: string; isLast: boolean }[] = [];
  for (let i = 0; i < pathParts.length; i++) {
    const part = pathParts[i];
    if (UUID_RE.test(part)) continue; // skip raw UUIDs
    // Collapse "user" + UUID into the page title instead
    const nextIsUuid = i + 1 < pathParts.length && UUID_RE.test(pathParts[i + 1]);
    if (part === 'user' && nextIsUuid) {
      breadcrumbs.push({ label: title, path: location.pathname, isLast: true });
      break;
    }
    breadcrumbs.push({
      label: part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '),
      path: '/' + pathParts.slice(0, i + 1).join('/'),
      isLast: false, // recalculated below
    });
  }
  // Append board title as final crumb if it differs from the last path segment
  const lastLabel = breadcrumbs[breadcrumbs.length - 1]?.label;
  if (title && lastLabel && lastLabel.toLowerCase() !== title.toLowerCase()) {
    breadcrumbs.push({ label: title, path: location.pathname, isLast: true });
  }
  // Mark the actual last crumb
  breadcrumbs.forEach((bc, i) => { bc.isLast = i === breadcrumbs.length - 1; });

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="w-full">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 1 && (
          <nav className="flex items-center gap-1 text-xs mb-4 opacity-60">
            {breadcrumbs.map((bc, i) => (
              <React.Fragment key={`crumb-${i}`}>
                {i > 0 && <FiChevronRight className="w-3 h-3" />}
                {bc.isLast ? (
                  <span className="font-medium">{bc.label}</span>
                ) : (
                  <Link to={bc.path} className="hover:underline">{bc.label}</Link>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}

        {/* Dashboard Nav Cards */}
        <DashboardNav />

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${BOARD_GRADIENTS[title] || 'from-blue-500 to-purple-600'} drop-shadow-sm`}>{title}</h1>
            {BOARD_SUBTITLES[title] && (
              <p className={`text-xs mt-0.5 italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{BOARD_SUBTITLES[title]}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {(['7d', '30d', '90d'] as DateRange[]).map(range => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
                  dateRange === range
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md shadow-purple-900/20'
                    : darkMode ? 'bg-gray-800/80 text-gray-300 hover:bg-gray-700 border border-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {range}
              </button>
            ))}
            <button
              onClick={handleRefresh}
              className={`p-1.5 rounded ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
              title="Refresh"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-2 py-1 text-xs rounded ${
                autoRefresh
                  ? 'bg-green-600/20 text-green-400'
                  : darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-500'
              }`}
            >
              Auto
            </button>
          </div>
        </div>

        {/* Gradient separator */}
        <div className={`h-px mb-5 bg-gradient-to-r from-transparent ${
          title === 'The Pulse' ? 'via-indigo-500/20' :
          title === 'Forge Monitor' ? 'via-purple-500/20' :
          title === 'Phase Watch' ? 'via-blue-500/20' :
          title === 'Blueprint Vault' ? 'via-cyan-500/20' : 'via-purple-500/20'
        } to-transparent`} />

        {children(dateRange)}
      </div>
    </div>
  );
};

export default DashboardShell;
